import type { PrefetchedDataProps, PrefetchedUserDataProps } from '#src/shared/prefetch';
import { withPrefetchedData } from '#src/shared/prefetch';

import {
	type PetitionDetailsPagePrefetchedContext,
	type PetitionDetailsPagePrefetchedUserContext,
	prefetchPageContext,
	prefetchPageUserContext,
} from '#src/app/pages/petitionDetails/pageContext';
import { isLoaded } from '#src/app/shared/utils/async';

import { CommonPetitionDetails } from '../CommonPetitionDetails';
import {
	buildMetaContext,
	petitionDetailsPageAlternateUrls,
	petitionDetailsPageCanonical,
	petitionDetailsPageMetadata,
	petitionDetailsPageSchemaOrg,
	petitionDetailsPageTitle,
} from '../shared';
import type { PetitionDetailsMetaContext } from '../shared';

const OG_TEXT_VARIANTS = ['variant_1', 'variant_2', 'variant_3'] as const;
type OgTextVariant = (typeof OG_TEXT_VARIANTS)[number];

const DESCRIPTION_MAX_LENGTH = 150;
// U+270D WRITING HAND + U+FE0F, so clients render the emoji and not the monochrome text glyph.
const WRITING_HAND = '✍️';

type PrefetchedData = PetitionDetailsPagePrefetchedContext &
	Readonly<{
		ogTextVariant: OgTextVariant | undefined;
		metaContext: PetitionDetailsMetaContext;
		bestShareVisual: boolean;
	}>;

function toOgTextVariant(value: string | undefined): OgTextVariant | undefined {
	return OG_TEXT_VARIANTS.find((variant) => variant === value);
}

/**
 * Cuts at the last word boundary before the limit, so the description never ends mid-word. Counts code points, so
 * text without spaces (e.g. Japanese) is never cut inside a surrogate pair.
 */
function truncateAtWord(text: string, maxLength: number): string {
	const codePoints = Array.from(text);
	if (codePoints.length <= maxLength) return text;
	const cut = codePoints.slice(0, maxLength).join('');
	const lastBreak = cut.search(/\s\S*$/);
	return `${(lastBreak > 0 ? cut.slice(0, lastBreak) : cut).trimEnd()}…`;
}

export const PetitionDetailsOgText = withPrefetchedData(
	function PetitionDetailsOgText({
		prefetchedData,
		prefetchedUserData,
	}: PrefetchedDataProps<PrefetchedData> & PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>) {
		return <CommonPetitionDetails prefetchedData={prefetchedData} prefetchedUserData={prefetchedUserData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			const {
				params: { slug, ogVariant },
			} = context;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const pageContext = await prefetchPageContext(slug!, context);
			return {
				...pageContext,
				ogTextVariant: toOgTextVariant(ogVariant),
				metaContext: buildMetaContext(context),
				bestShareVisual: context.path.endsWith('/bsv'),
			};
		},
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
		pageTitle: (ctx) => petitionDetailsPageTitle(ctx),
		pageCanonical: (ctx) => petitionDetailsPageCanonical(ctx),
		pageMetadata: ({ prefetchedData, i18n, getStaticAssetUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { ask, descriptionStripped } = prefetchedData.pageData.pageData.petition;
			const title = (() => {
				switch (prefetchedData.ogTextVariant) {
					case 'variant_1':
						return ask;
					case 'variant_2':
						return i18n.translate('corgi.petition_details.share_preview.sign_title', { petitionTitle: ask });
					case 'variant_3':
						return `${WRITING_HAND} ${ask}`;
					default:
						return undefined;
				}
			})();
			return petitionDetailsPageMetadata({
				prefetchedData,
				i18n,
				getStaticAssetUrl,
				metaContext: prefetchedData.metaContext,
				bestShareVisual: prefetchedData.bestShareVisual,
				shareHeadlineOverrides: title
					? {
							title,
							description: descriptionStripped ? truncateAtWord(descriptionStripped, DESCRIPTION_MAX_LENGTH) : ask,
						}
					: undefined,
			});
		},
		pageSchemaOrg: (ctx) =>
			petitionDetailsPageSchemaOrg({
				prefetchedData: ctx.prefetchedData,
				resolveUrl: ctx.resolveUrl,
				defineArticle: ctx.defineArticle,
				defineBreadcrumb: ctx.defineBreadcrumb,
				definePerson: ctx.definePerson,
			}),
		pageAlternateUrls: (ctx) => petitionDetailsPageAlternateUrls(ctx),
	},
);
